import {
  type AgentDetails,
  Team,
  TeamMember,
  useGetAgencyMembers,
  useGetTeamsForAgency,
  useGetUrbanXTeamForAgency,
  useInvalidateUseGetAgencyMembers,
  useInvalidateUseGetTeamsForAgency,
  useInvalidateUseGetUrbanXTeamForAgency,
  useSetTeamsForAgency,
} from '@urbanx/agx.agencies.client';
import { StaffRole } from '@urbanx/agx.auth.client';
import { useGetStaffByRoles } from '@urbanx/agx.staff.client';
import {
  AgxAvatar,
  AgxButton,
  AgxDivider,
  AgxDropdown,
  AgxNotificationBox,
  AgxPanel,
  AgxSpacer,
  AgxSaveChangesButton,
  SAVE_BUTTON_STATES,
  Icon_Arrow,
  SelectChipOption,
  useToast,
} from '@urbanx/agx.ui-components2';
import LoadingState from 'components/loadingState/LoadingState';
import { getTeamSaveErrorMessage } from './getTeamSaveErrorMessage';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SortableTeamMembers from './SortableTeamMembers';
import { AgxFormTextField, saveButtonStateFor } from '@urbanx/agx.forms';
import { TeamForm, teamFormValues } from './teamFormDefinition';

import * as styles from './AddOrEditTeamPage.css';
import CommunicationPreferences from './CommunicationPreferences';
import PhotoUpload from '../PhotoUpload';
import * as pageStyles from '../teamEditorPage.css';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';

interface AddOrEditTeamPageProps {
  isEdit?: boolean;
}

const BoundAddOrEditTeamPage = ({ isEdit = false }: AddOrEditTeamPageProps) => {
  const { pushToast } = useToast();
  const { teamId } = useParams();
  const { agencyId } = useParams();
  const { data: adminManagers = [], isPending: isStaffPending } = useGetStaffByRoles([
    StaffRole.AdminManager,
  ]);
  const { data: urbanXTeam, isPending: isUrbanXTeamPending } = useGetUrbanXTeamForAgency(
    agencyId ?? '',
    undefined,
    !!agencyId
  );
  const defaultAdminManager = urbanXTeam?.adminManager;
  const defaultAdminManagerName = defaultAdminManager
    ? `${defaultAdminManager.firstName} ${defaultAdminManager.lastName}`
    : undefined;
  const adminManagerOptions = [
    {
      value: '',
      label: 'Agency default',
      description: defaultAdminManagerName ?? 'Not set on URBAN X Team',
    },
    ...adminManagers.map((member) => ({
      value: member.id ?? '',
      label: `${member.firstName} ${member.lastName}`,
      description: member.email,
    })),
  ];

  const { data: teamsForAgency, isPending: isTeamsForAgencyPending } = useGetTeamsForAgency(
    agencyId ?? '',
    !!agencyId
  );

  const { data: agencyMembers, isPending: isAgencyMembersPending } = useGetAgencyMembers(
    agencyId ?? '',
    !!agencyId
  );

  const isAnythingLoading =
    isTeamsForAgencyPending || isAgencyMembersPending || isStaffPending || isUrbanXTeamPending;

  const selectedTeam =
    isEdit && teamId ? teamsForAgency?.find((team) => team.id === teamId) : undefined;

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [photoUploadPending, setPhotoUploadPending] = useState(false);
  const copiedMemberName = useRef<string | undefined>(undefined);
  const [createdTeam, setCreatedTeam] = useState<Team>();
  const sourceTeam = isEdit ? selectedTeam : createdTeam;
  const invalidateTeamsForAgency = useInvalidateUseGetTeamsForAgency();
  const invalidateAgencyMembers = useInvalidateUseGetAgencyMembers();
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency();
  const { setTeamsForAgency, setTeamsForAgencyPending } = useSetTeamsForAgency({
    onSuccess: async () => {
      await Promise.all([
        invalidateTeamsForAgency(agencyId ?? ''),
        invalidateAgencyMembers(agencyId ?? ''),
        invalidateUrbanXTeamForAgency(agencyId ?? ''),
      ]);
      pushToast({
        type: 'success',
        message: isEdit ? 'Changes saved successfully' : 'Changes Saved',
      });
    },
    onError: (error) => {
      pushToast({ type: 'error', message: getTeamSaveErrorMessage(error) });
    },
  });
  const form = TeamForm.useForm({
    defaultValues: teamFormValues(sourceTeam),
    onSubmit: async ({ parsedValues }) => {
      if (!form.isDirty) return;
      if (
        parsedValues.name !== sourceTeam?.name &&
        copiedMemberName.current !== parsedValues.name &&
        teamsForAgency?.some(
          (team) => team.name === parsedValues.name && (!isEdit || team.id !== teamId)
        )
      ) {
        return {
          status: 'rejected',
          issues: [
            {
              path: 'name',
              code: 'duplicate_name',
              message: 'Team name already exists.',
              source: 'server',
            },
          ],
        };
      }
      const submittedTeam: Team = { ...selectedTeam, ...parsedValues };
      const updatedTeams = isEdit
        ? (teamsForAgency ?? []).map((team) => (team.id === teamId ? submittedTeam : team))
        : [...(teamsForAgency ?? []), submittedTeam];
      try {
        await setTeamsForAgency({ agencyId: agencyId ?? '', agencyTeams: updatedTeams });
        if (!isEdit) setCreatedTeam(submittedTeam);
        return { status: 'accepted' };
      } catch (error) {
        // The mutation callback presents the failure; retain the submitted draft for retry.
        return { status: 'failed', error };
      }
    },
  });
  const teamMembers: TeamMember[] = form.values.members;
  const orderedMembersList = teamMembers.map((member) => member.agentId!);

  const onTeamMemberSelectionChanged = (
    options: SelectChipOption[],
    onChange: (members: TeamMember[]) => void
  ) => {
    const selectedOptions = options.filter((o) => o.selected).map((o) => o.value);

    const selectedTeamMembers =
      agencyMembers?.filter((member) => selectedOptions.includes(member.id!)) ?? [];

    const newMembers: TeamMember[] = selectedTeamMembers
      .filter((member) => !teamMembers.some((m) => m.agentId === member.id))
      .map((member) => ({
        agentId: member.id!,
        communicationPreferences: undefined,
      }));

    const existingMembers = teamMembers.filter((member) =>
      selectedOptions.includes(member.agentId!)
    );

    onChange([...existingMembers, ...newMembers]);
  };

  const potentialMembersList: SelectChipOption[] =
    agencyMembers?.map((member) => ({
      value: member.id || '',
      label: `${member.firstName} ${member.lastName}`,
      icon: <MemberAvatar member={member} />,
      selected: teamMembers.some((m) => m.agentId === member.id),
    })) || [];

  const onTeamMemberOrderUpdated = (
    newOrder: string[],
    onChange: (members: TeamMember[]) => void
  ) => {
    const newOrderedMembers = newOrder
      .map((id) => teamMembers.find((member) => member.agentId === id))
      .filter((member): member is TeamMember => member !== undefined);

    onChange(newOrderedMembers);
  };

  const selectedAdminManager = adminManagers.find(
    (member) => member.id === form.values.adminManagerId
  );
  const adminManagerHintText = selectedAdminManager
    ? `Campaigns led by members of this team will use ${selectedAdminManager.firstName} ${selectedAdminManager.lastName} instead of the agency default.`
    : defaultAdminManagerName
      ? `Campaigns led by members of this team use the agency default, ${defaultAdminManagerName}.`
      : 'Campaigns led by members of this team use the agency default Admin Manager.';
  const membersInOtherTeams = potentialMembersList.filter(
    (member) =>
      member.selected &&
      teamsForAgency?.some(
        (team) =>
          team.id !== sourceTeam?.id &&
          team.members?.some((otherMember) => otherMember.agentId === member.value)
      )
  );
  const membersInOtherTeamsNames = new Intl.ListFormat('en-AU').format(
    membersInOtherTeams.map((member) => member.label)
  );

  const onConfirmDeleteTeamClicked = async () => {
    if (!isEdit) {
      setShowDeleteConfirmation(false);
      return;
    }

    const updatedTeams = teamsForAgency?.filter((team) => team.id !== teamId) ?? [];

    await setTeamsForAgency({
      agencyId: agencyId ?? '',
      agencyTeams: updatedTeams,
    });

    setShowDeleteConfirmation(false);
  };

  if (isAnythingLoading || (isEdit && !selectedTeam)) {
    return <LoadingState />;
  }

  return (
    <TeamForm.Root form={form} className={styles.form}>
      <AgxSpacer fill gap="spacing-3xl" className={pageStyles.page}>
        <AgxSpacer
          direction="horizontal"
          wrap
          fill
          gap="spacing-lg"
          contentJustification="space-between"
          contentAlignment="center"
        >
          <AgxButton
            id="return-button"
            variant="naked"
            text={`${isEdit ? 'Edit' : 'Add'} Team`}
            leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
            renderLink={(props) => <Link to="../manage-teams" {...props} />}
          />
          <div className={pageStyles.actions}>
            {isEdit && (
              <AgxButton
                id="deleteTeam"
                className={pageStyles.action}
                wrapperClassName={pageStyles.action}
                text="Delete Team"
                variant="naked"
                size="medium"
                danger
                onClick={() => setShowDeleteConfirmation(true)}
              />
            )}
            <AgxSaveChangesButton
              id="save-team-button"
              className={pageStyles.action}
              wrapperClassName={pageStyles.action}
              text="Save Changes"
              variant="primary"
              size="medium"
              state={
                setTeamsForAgencyPending
                  ? SAVE_BUTTON_STATES.SAVING
                  : saveButtonStateFor(form.phase, form.isDirty)
              }
              onClick={() => void form.submit()}
              disabled={!form.isDirty || !form.canSubmit || setTeamsForAgencyPending}
            />
          </div>
        </AgxSpacer>
        <AgxDivider variant="horizontal" />
        <TeamForm.CustomField name="members">
          {(membersField) => (
            <div className={styles.panels}>
              <AgxPanel header="Team information">
                <AgxSpacer fill gap="spacing-2xl">
                  <TeamForm.Field
                    name="name"
                    Input={AgxFormTextField}
                    onValueChange={(name) => {
                      if (name !== copiedMemberName.current) copiedMemberName.current = undefined;
                    }}
                    type="text"
                    label="Team name"
                    size="medium"
                    hideOptionalText
                    placeholder="Team 1"
                    stretch
                  />
                  <AgxDropdown
                    id="team-members-select-chip"
                    label="Team members"
                    type="multi"
                    stretch
                    hideOptionalText
                    selected={membersField.value.map((member) => member.agentId!)}
                    options={potentialMembersList}
                    onChange={(ids) =>
                      onTeamMemberSelectionChanged(
                        potentialMembersList.map((option) => ({
                          ...option,
                          selected: ids.includes(option.value),
                        })),
                        membersField.onChange
                      )
                    }
                  />
                  <SortableTeamMembers
                    orderedMembersList={orderedMembersList}
                    agencyMembers={agencyMembers ?? []}
                    orderUpdated={(order) => onTeamMemberOrderUpdated(order, membersField.onChange)}
                  />
                  <TeamForm.CustomField name="adminManagerId">
                    {(field) => (
                      <AgxDropdown
                        id="team-admin-manager"
                        label="Admin Manager"
                        options={adminManagerOptions}
                        selected={field.value ?? ''}
                        onChange={(value) => field.onChange(value || undefined)}
                        hintText={adminManagerHintText}
                        disabled={field.disabled}
                        searchable
                        stretch
                      />
                    )}
                  </TeamForm.CustomField>
                  {selectedAdminManager && membersInOtherTeams.length > 0 && (
                    <AgxNotificationBox
                      type="information"
                      heading={`${membersInOtherTeams.length} ${membersInOtherTeams.length === 1 ? 'member is' : 'members are'} also in another team`}
                      body={`Campaigns led by ${membersInOtherTeamsNames} will use the agency default${defaultAdminManagerName ? ` (${defaultAdminManagerName})` : ''}, because their team can't be determined.`}
                    />
                  )}
                </AgxSpacer>
              </AgxPanel>
              <AgxPanel header="Team photo">
                <TeamForm.CustomField name="photoUri">
                  {(field) => (
                    <AgxSpacer fill gap="spacing-2xl">
                      <PhotoUpload
                        photoUri={field.value}
                        agencyId={agencyId ?? ''}
                        disabled={field.disabled}
                        onUploaded={(document) => field.onChange(document.publicPath)}
                        onRemove={() => field.onChange(undefined)}
                        onUploadPendingChange={setPhotoUploadPending}
                      />
                      <AgxDropdown
                        id="team-member-photo"
                        label="Or use from team member:"
                        placeholder="Select team member"
                        hideOptionalText
                        stretch
                        // A completing upload would otherwise overwrite a member photo picked meanwhile.
                        disabled={field.disabled || photoUploadPending}
                        options={potentialMembersList.filter((member) => member.selected)}
                        onChange={(id) => {
                          const member = agencyMembers?.find((agent) => agent.id === id);
                          if (!member) return;
                          copiedMemberName.current = `${member.firstName} ${member.lastName}`;
                          form.form.setFieldValue('name', copiedMemberName.current);
                          form.clearServerIssuesForPath('name');
                          field.onChange(member.photo?.publicPath ?? '');
                        }}
                      />
                    </AgxSpacer>
                  )}
                </TeamForm.CustomField>
              </AgxPanel>
              <AgxPanel header="Communication Preferences" className={styles.preferences}>
                <CommunicationPreferences
                  members={membersField.value}
                  onPreferencesChange={membersField.onChange}
                  agents={agencyMembers ?? []}
                />
              </AgxPanel>
            </div>
          )}
        </TeamForm.CustomField>
        <DeleteConfirmation
          name={selectedTeam?.name || ''}
          title="Delete team?"
          lineOne="Are you sure you want to delete"
          lineTwo="This cannot be undone."
          primaryActionTitle="Permanently delete team"
          secondaryActionTitle="No, cancel"
          showPopUp={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={onConfirmDeleteTeamClicked}
        />
      </AgxSpacer>
    </TeamForm.Root>
  );
};

const MemberAvatar = ({ member }: { member: AgentDetails }) =>
  member.photo?.publicPath ? (
    <AgxAvatar type="image" size="2xs" imageUri={member.photo.publicPath} />
  ) : (
    <AgxAvatar
      type="text-dark"
      size="2xs"
      text={`${member.firstName?.[0] ?? ''}${member.lastName?.[0] ?? ''}`.toUpperCase()}
    />
  );

const AddOrEditTeamPage = (props: AddOrEditTeamPageProps) => {
  const { agencyId, teamId } = useParams();
  return <BoundAddOrEditTeamPage key={`${agencyId}:${props.isEdit ? teamId : 'new'}`} {...props} />;
};

export default AddOrEditTeamPage;
